import { AnyAction } from "redux"
import { ThunkAction } from "redux-thunk"
import {
    SharedStoreState,
    TOrganizationType,
    addModalThunk,
    endpointsShared,
    getFilesFromFileList,
    getText,
    requestThunk,
} from ".."
import { kycActions, kycSelectors } from "../store/kycSlice"
import {
    EKycDetailsType,
    EKycEventType,
    EKycFlowType,
    IKycFlowDto,
    IKycOrgDetails,
    IKycWebshopDetails,
} from "../types/kyc/kycFlowTypes"

export const fetchKycFlowsThunk =
    (
        organizationId: string,
        organizationType: TOrganizationType,
        force: boolean
    ): ThunkAction<void, SharedStoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        if (!force && kycSelectors.flows(getState(), organizationId)) {
            return
        }

        const resp = await dispatch(
            requestThunk<{ organizationId: string; organizationType: TOrganizationType; flows: IKycFlowDto[] }>(
                endpointsShared.KYC.getFlow,
                {
                    params: { organizationId, organizationType },
                }
            )
        )

        dispatch(
            kycActions.setFlows({
                organizationId,
                flows: resp.flows,
            })
        )
    }

export const fetchKycDetailsThunk =
    (
        organizationId: string,
        organizationType: TOrganizationType,
        webshopId: string | undefined,
        force: boolean
    ): ThunkAction<Promise<void>, SharedStoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        if (!force && kycSelectors.orgDetails(getState(), organizationId)) {
            return
        }

        const resp = await dispatch(
            requestThunk<{
                organizationId: string
                organizationType: TOrganizationType
                webshopId: string | undefined
                organization: IKycOrgDetails
                webshop?: IKycWebshopDetails
            }>(endpointsShared.KYC.getDetails, {
                params: { organizationId, organizationType, webshopId },
            })
        )

        dispatch(
            kycActions.orgDetails({
                organizationId,
                details: resp.organization || {},
            })
        )
        if (webshopId) {
            dispatch(
                kycActions.webshopDetails({
                    webshopId: webshopId,
                    details: resp.webshop || {},
                })
            )
        }
    }

interface KycUpdateFlowResponse {
    initializationLink?: string
    organization: IKycOrgDetails
    webshop: IKycWebshopDetails | undefined
    flows: IKycFlowDto[]
}
export const initiateKycFlowThunk =
    (
        organizationId: string,
        organizationType: TOrganizationType,
        webshopId: string | undefined,
        type: EKycFlowType
    ): ThunkAction<void, SharedStoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        const resp = await dispatch(
            requestThunk<KycUpdateFlowResponse>(endpointsShared.KYC.updateFlow, {
                data: {
                    organizationId,
                    organizationType,
                    webshopId,
                    flowType: type,
                    eventType: "Initiated" satisfies EKycEventType,
                },
            })
        )

        dispatch(
            kycActions.setFlows({
                organizationId,
                flows: resp.flows,
            })
        )

        if (resp.organization) {
            dispatch(
                kycActions.orgDetails({
                    organizationId,
                    details: resp.organization,
                })
            )
        }
        if (webshopId) {
            dispatch(
                kycActions.webshopDetails({
                    webshopId: webshopId,
                    details: resp.webshop,
                })
            )
        }
    }

export const updateKycFlowThunk =
    (
        flow: IKycFlowDto,
        eventType: EKycEventType,
        orgDetails: IKycOrgDetails,
        webshopDetails?: IKycWebshopDetails
    ): ThunkAction<Promise<IKycOrgDetails>, SharedStoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        const resp = await dispatch(
            requestThunk<KycUpdateFlowResponse>(
                endpointsShared.KYC.updateFlow,
                {
                    data: {
                        organizationId: flow.organizationId,
                        organizationType: flow.organizationType,
                        organization: orgDetails,
                        webshop: webshopDetails,
                        webshopId: flow.webshopId,
                        flowType: flow.flowType,
                        eventType: eventType,
                    },
                },
                {
                    errorHandlers: [
                        {
                            handleError: async (err, dispatch) => {
                                if (err.errorCode?.includes("EmailExistsForOtherCompany")) {
                                    await dispatch(
                                        addModalThunk({
                                            type: "error",
                                            errorMessage: getText("common.email.takenByOtherCompany"),
                                        })
                                    )
                                    return true
                                }
                                return false
                            },
                        },
                    ],
                }
            )
        )

        dispatch(
            kycActions.setFlows({
                organizationId: flow.organizationId,
                flows: resp.flows,
            })
        )

        if (resp.organization) {
            dispatch(
                kycActions.orgDetails({
                    organizationId: flow.organizationId,
                    details: resp.organization,
                })
            )
        }
        if (flow.webshopId) {
            dispatch(
                kycActions.webshopDetails({
                    webshopId: flow.webshopId,
                    details: resp.webshop,
                })
            )
        }
        return resp.organization
    }

export type EFileType = "IdCard" | "PersonWithIdCard" | "BankAccountVerification" | "CompanyAssociation"

export interface IUploadFileModel {
    relationId: string
    files: FileList
    type: EFileType
    webshopId?: string
    kycPersonId?: string
    email?: string
    kycFlowId?: string
    kycDetailsType?: EKycDetailsType
}

export const uploadKycFilesThunk =
    (model: IUploadFileModel): ThunkAction<Promise<void>, SharedStoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        const { files, ...baseModel } = model
        const filesToUpload = getFilesFromFileList(files)
        await Promise.all(
            filesToUpload.map((f) =>
                dispatch(
                    requestThunk<void>(
                        endpointsShared.KYC.uploadFile,
                        {
                            data: {
                                ...baseModel,
                                file: f,
                            },
                        },
                        { asFormData: true }
                    )
                )
            )
        )
    }

export const deleteKycFileThunk =
    (relationId: string, fileId: string): ThunkAction<Promise<void>, SharedStoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        await dispatch(
            requestThunk<void>(endpointsShared.KYC.deleteFile, {
                data: {
                    relationId,
                    fileId,
                },
            })
        )
    }
